import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Input } from "antd";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Moment } from "moment";
import getValidationErrors from "../../../../utils/getValidationErrors";
import FormItem from "../../../../components/FormItem";
import { useToast } from "../../../../hooks/toast";
import api from "../../../../services/api";
import { removeNullKeys } from "../../../../utils/formatters";
import { IUser } from "../../../Users";
import { IRequirement } from "../..";
import ActivityTypes from "../../../../enums/ActivityTypes";

interface IActivityModalProps {
  visible: boolean;
  activity?: IRequirement;
  handleClose: (activity?: IRequirement) => void;
}

interface IHoursFactory {
  description: string;
  id: string;
  value_hour: number;
}

interface IActivityFormData {
  identifier: string;
  description: string;
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const ActivityEditModal: React.FC<IActivityModalProps> = ({
  visible,
  activity,
  handleClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [userFilter, setUserFilter] = useState<string>();
  const { t } = useTranslation(["activities"]);
  const { addToast } = useToast();
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      setValidationErrors({});
    }
  }, [visible]);

  const handleSubmit = useCallback(
    async (formData: IActivityFormData) => {
      const data = removeNullKeys(formData);
      try {
        const schema = Yup.object().shape({
          identifier: Yup.string(),
          description: Yup.string().required("Descrição obrigatória"),
        });

        await schema.validate(
          { ...data, consultant_id: userFilter },
          {
            abortEarly: false,
          },
        );

        setLoading(true);

        const { identifier, description } = data;

        const payload = {
          identifier,
          description,
        };

        // Call the api to edit the activity
        await api.patch(
          `/activities/${activity?.id}/edit/description`,
          payload,
        );

        const editActivity = ({
          ...activity,
          ...payload,
        } as unknown) as IRequirement;

        handleClose(editActivity);

        addToast({
          type: "info",
          title: "Atividade pendete adicionada",
          description: "As atividades pendentes precisam ser confirmadas.",
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
          return;
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, userFilter, activity],
  );

  useEffect(() => {
    if (activity) {
      form.setFieldsValue({
        identifier: activity.identifier,
        description: activity.description,
      });
    }
  }, [activity, form]);

  return (
    <Modal
      title={t("actions.options.edit")}
      visible={visible}
      width={600}
      onOk={() => form.submit()}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      forceRender
    >
      <Form
        {...layout}
        form={form}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        <FormItem
          name="identifier"
          label={t("identifier")}
          initialValue={activity?.identifier}
        >
          <Input />
        </FormItem>

        <FormItem
          name="description"
          required
          label={t("description")}
          errors={validationErrors}
          initialValue={activity?.description}
        >
          <Input />
        </FormItem>
      </Form>
    </Modal>
  );
};

export default ActivityEditModal;
